import { USER_REGION_CODE_KEY } from './auth-storage'
import { ApiError } from './types'
import { http } from './request'
import { msg } from '@/plugins/message'
import {
  normalizeAdminDivisionCodeToNine,
  resolveAdminDivisionTier,
  type AdminDivisionTier,
} from '@/utils/adminDivisionHierarchy'
import { secureLocalStorage } from '@/utils/secureLocalStorage'

export { normalizeAdminDivisionCodeToNine } from '@/utils/adminDivisionHierarchy'

export interface RegionCountyNode {
  name: string
  code: string
  geonameid?: number
}

export interface RegionPrefectureNode {
  name: string
  code: string
  geonameid?: number
  county_level: RegionCountyNode[]
}

export interface RegionProvinceNode {
  province: string
  code: string
  geonameid?: number
  prefecture_level: RegionPrefectureNode[]
}

/** 与后端 `AdministrativeAreaTreeKeyResult` 一致：行政区划目录树节点 */
export interface AdministrativeAreaTreeKeyResult {
  geonameid?: number
  parentGeonameid?: number | null
  cnName?: string | null
  regionalLevel?: number | null
  adminDivisionCode?: string | null
  addressCodeLevel?: string | null
  children?: AdministrativeAreaTreeKeyResult[]
}

/** 本地未写入 `user_regionCode` 时的默认根区划码（贵州省本级及下级），与 `administrativeAreaTree` 入参一致 */
export const GUIZHOU_ROOT_ADMINISTRATIVE_CODE = '520000000'

/** `administrativeAreaTree` 的 rootAdministrativeCode：优先 localStorage `user_regionCode`（登录用户行政区），否则贵州省根码 */
export function getRootAdministrativeCodeForTree(): string {
  try {
    const s = secureLocalStorage.getItem(USER_REGION_CODE_KEY)
    const t = s != null ? String(s).trim() : ''
    if (t) return t
  } catch {
    // ignore
  }
  return GUIZHOU_ROOT_ADMINISTRATIVE_CODE
}

/** 优先 GlobalCountryController；404 等失败时再试 CommonController（不同部署可能只暴露其一） */
const ADMINISTRATIVE_AREA_TREE_PATHS = [
  '/biz/overseas/globalcountry/administrativeAreaTree',
  '/biz/overseas/common/globalcountry/administrativeAreaTree',
] as const

/** 地区增删改：与树接口同样做双路径兼容 */
const AREA_ADD_PATHS = [
  '/biz/overseas/globalcountry/area/add',
  '/biz/overseas/common/globalcountry/area/add',
] as const
const AREA_EDIT_PATHS = [
  '/biz/overseas/globalcountry/area/edit',
  '/biz/overseas/common/globalcountry/area/edit',
] as const
const AREA_DELETE_PATHS = [
  '/biz/overseas/globalcountry/area/delete',
  '/biz/overseas/common/globalcountry/area/delete',
] as const

/** 树接口成功路径前缀，增删改优先走同一套 Controller，减少 404 探测 */
type AreaApiBase = '/biz/overseas/globalcountry' | '/biz/overseas/common/globalcountry'
let preferredAreaApiBase: AreaApiBase | null = null

function rememberAreaApiBaseFromTreePath(path: string) {
  if (path.includes('/common/globalcountry')) {
    preferredAreaApiBase = '/biz/overseas/common/globalcountry'
  } else {
    preferredAreaApiBase = '/biz/overseas/globalcountry'
  }
}

function orderPathsByPreferredBase(paths: readonly string[]): string[] {
  if (!preferredAreaApiBase) return [...paths]
  const preferred: string[] = []
  const rest: string[] = []
  for (const p of paths) {
    if (p.startsWith(preferredAreaApiBase)) preferred.push(p)
    else rest.push(p)
  }
  return [...preferred, ...rest]
}

function isMissingApiPathError(e: unknown): boolean {
  if (e instanceof ApiError) {
    if (e.code === 404 || e.status === 404) return true
    return /资源路径不存在|Not Found|404/i.test(e.message)
  }
  return false
}

async function postWithPathFallback(paths: readonly string[], body: unknown): Promise<void> {
  const ordered = orderPathsByPreferredBase(paths)
  let lastError: unknown
  for (let i = 0; i < ordered.length; i++) {
    const path = ordered[i]!
    const isLast = i === ordered.length - 1
    try {
      // 非末次：静默探测路径是否存在；业务错误仍向上抛并由下方补提示
      await http.post<void>(path, body, { skipErrorMessage: true })
      if (path.includes('/common/globalcountry')) {
        preferredAreaApiBase = '/biz/overseas/common/globalcountry'
      } else {
        preferredAreaApiBase = '/biz/overseas/globalcountry'
      }
      return
    } catch (e) {
      lastError = e
      if (!isLast && isMissingApiPathError(e)) continue
      if (e instanceof ApiError && e.message) {
        msg.error(e.message)
      }
      throw e
    }
  }
  throw lastError
}

function pickRecordField(o: Record<string, unknown>, camel: string, snake: string): unknown {
  const v = o[camel]
  if (v !== undefined && v !== null && v !== '') return v
  return o[snake]
}

/** 将接口返回（含 snake_case、单根对象、误包一层 data）规范为节点数组 */
function coerceAdminTreeNode(raw: unknown): AdministrativeAreaTreeKeyResult | null {
  if (raw == null || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const cnRaw = pickRecordField(o, 'cnName', 'cn_name')
  const codeRaw = pickRecordField(o, 'adminDivisionCode', 'admin_division_code')
  const geoRaw = pickRecordField(o, 'geonameid', 'geonameid')
  const childrenRaw = o.children
  const childList = Array.isArray(childrenRaw) ? childrenRaw : []
  const children = childList
    .map(coerceAdminTreeNode)
    .filter((x): x is AdministrativeAreaTreeKeyResult => x != null)
  let geonameid: number | undefined
  if (geoRaw != null && geoRaw !== '') {
    const n = Number(geoRaw)
    if (!Number.isNaN(n)) geonameid = n
  }
  const node: AdministrativeAreaTreeKeyResult = {
    cnName: cnRaw != null && cnRaw !== '' ? String(cnRaw) : null,
    adminDivisionCode: codeRaw != null && codeRaw !== '' ? String(codeRaw) : null,
    geonameid,
    children,
  }
  if (
    node.cnName == null &&
    node.adminDivisionCode == null &&
    node.geonameid == null &&
    children.length === 0
  ) {
    return null
  }
  return node
}

function normalizeAdministrativeTreeRoots(raw: unknown): AdministrativeAreaTreeKeyResult[] {
  if (raw == null) return []
  if (Array.isArray(raw)) {
    return raw
      .map(coerceAdminTreeNode)
      .filter((x): x is AdministrativeAreaTreeKeyResult => x != null)
  }
  if (typeof raw === 'object') {
    const o = raw as Record<string, unknown>
    if (Array.isArray(o.data)) {
      return normalizeAdministrativeTreeRoots(o.data)
    }
    const one = coerceAdminTreeNode(raw)
    return one ? [one] : []
  }
  return []
}

function administrativeRootToRegionProvince(
  root: AdministrativeAreaTreeKeyResult,
): RegionProvinceNode {
  const provinceCode = String(root.adminDivisionCode ?? root.geonameid ?? '')
  const hasIdentity = (n: AdministrativeAreaTreeKeyResult) =>
    (n.cnName != null && n.cnName !== '') ||
    (n.adminDivisionCode != null && n.adminDivisionCode !== '') ||
    (n.geonameid != null && !Number.isNaN(n.geonameid))

  const prefecture_level: RegionPrefectureNode[] = (root.children ?? [])
    .filter((c) => c && hasIdentity(c))
    .map((city) => {
      const county_level: RegionCountyNode[] = (city.children ?? [])
        .filter((x) => x && hasIdentity(x))
        .map((county) => ({
          name: String(county.cnName ?? ''),
          code: String(county.adminDivisionCode ?? county.geonameid ?? ''),
          geonameid: county.geonameid,
        }))
      return {
        name: String(city.cnName ?? ''),
        code: String(city.adminDivisionCode ?? city.geonameid ?? ''),
        geonameid: city.geonameid,
        county_level,
      }
    })
  return {
    province: String(root.cnName ?? ''),
    code: provinceCode,
    geonameid: root.geonameid,
    prefecture_level,
  }
}

function administrativeResponseToProvinces(
  roots: AdministrativeAreaTreeKeyResult[],
): RegionProvinceNode[] {
  return roots.map(administrativeRootToRegionProvince)
}

async function requestAdministrativeAreaTree(
  rootAdministrativeCode: string,
): Promise<AdministrativeAreaTreeKeyResult[]> {
  let lastError: unknown
  for (const path of ADMINISTRATIVE_AREA_TREE_PATHS) {
    try {
      const raw = await http.get<unknown>(path, {
        params: { rootAdministrativeCode },
        skipErrorMessage: true,
      })
      rememberAreaApiBaseFromTreePath(path)
      return normalizeAdministrativeTreeRoots(raw)
    } catch (e) {
      lastError = e
    }
  }
  if (lastError instanceof ApiError && lastError.message) {
    msg.error(lastError.message)
  }
  throw lastError
}

/**
 * 按根区划码拉取行政区划树并转为省-市-区结构。
 * 与当前「树默认根」（本地 `user_regionCode` 或贵州省根）相同时走 `fetchRegionTree` 的内存缓存。
 */
export async function fetchAdministrativeAreaTreeAsRegionProvinces(
  rootAdministrativeCode: string = getRootAdministrativeCodeForTree(),
): Promise<RegionProvinceNode[]> {
  if (rootAdministrativeCode === getRootAdministrativeCodeForTree()) {
    return fetchRegionTree()
  }
  try {
    const roots = await requestAdministrativeAreaTree(rootAdministrativeCode)
    return administrativeResponseToProvinces(roots)
  } catch {
    return []
  }
}

function flattenRegionNames(regions: RegionProvinceNode[]): string[] {
  const names: string[] = []
  for (const province of regions) {
    for (const city of province.prefecture_level ?? []) {
      for (const county of city.county_level ?? []) {
        names.push(`${province.province}-${city.name}-${county.name}`)
      }
    }
  }
  return names
}

function sameRegionCode(a: string | number | undefined | null, b: string): boolean {
  return String(a ?? '').trim() === b
}

function trimCodePath(codes: string[]): string[] {
  return codes.map((s) => String(s ?? '').trim()).filter(Boolean)
}

function matchRegionCodesFromLabelSlice(
  tree: RegionProvinceNode[],
  slice: string[],
): string[] | null {
  if (!slice.length) return null
  const province = tree.find((p) => p.province === slice[0])
  if (!province) return null
  const codes: string[] = [province.code]
  if (slice.length === 1) return codes
  const city = province.prefecture_level?.find((c) => c.name === slice[1])
  if (!city) return null
  codes.push(city.code)
  if (slice.length === 2) return codes
  const county = city.county_level?.find((c) => c.name === slice[2])
  if (!county) return null
  codes.push(county.code)
  return codes
}

/** 「省 / 市 / 区」名称路径 → 区划码路径；截断树按文案后缀匹配 */
export function findRegionCodesByLabelPath(
  tree: RegionProvinceNode[],
  labels: string[],
): string[] | null {
  const trimmed = labels.map((s) => s.trim()).filter(Boolean)
  if (!trimmed.length) return null
  for (let start = 0; start < trimmed.length; start++) {
    const hit = matchRegionCodesFromLabelSlice(tree, trimmed.slice(start))
    if (hit) return hit
  }
  return null
}

type ResolvedRegionPath = {
  labels: string[]
  lastCode: string
  hasDeeper: boolean
}

function resolveRegionPathByCodes(
  tree: RegionProvinceNode[],
  codes: string[],
): ResolvedRegionPath | null {
  const trimmed = trimCodePath(codes)
  if (!trimmed.length || trimmed.length > 3) return null
  const province = tree.find((p) => sameRegionCode(p.code, trimmed[0]!))
  if (!province) return null
  if (trimmed.length === 1) {
    return {
      labels: [province.province],
      lastCode: String(province.code),
      hasDeeper: (province.prefecture_level ?? []).length > 0,
    }
  }
  const city = (province.prefecture_level ?? []).find((c) => sameRegionCode(c.code, trimmed[1]!))
  if (!city) return null
  if (trimmed.length === 2) {
    return {
      labels: [province.province, city.name],
      lastCode: String(city.code),
      hasDeeper: (city.county_level ?? []).length > 0,
    }
  }
  const county = (city.county_level ?? []).find((c) => sameRegionCode(c.code, trimmed[2]!))
  if (!county) return null
  return {
    labels: [province.province, city.name, county.name],
    lastCode: String(county.code),
    hasDeeper: false,
  }
}

export function findRegionLabelsByCodePath(
  tree: RegionProvinceNode[],
  codes: string[],
): string[] | null {
  return resolveRegionPathByCodes(tree, codes)?.labels ?? null
}

function isDistrictLikeAdminCode(code: string): boolean {
  const tier = resolveAdminDivisionTier(code)
  return tier === 'district' || tier === 'unknown'
}

/** 是否已选到当前可见树的区县叶子（省级 3 级、市级 2 级、区级 1 级） */
export function isHouseholdRegionPathComplete(
  tree: RegionProvinceNode[],
  codes: string[],
): boolean {
  const hit = resolveRegionPathByCodes(tree, codes)
  if (!hit || hit.hasDeeper) return false
  return isDistrictLikeAdminCode(hit.lastCode)
}

/** 按任意一级区划码解析级联路径 */
export function findRegionPathByAdministrativeCode(
  tree: RegionProvinceNode[],
  code: string,
): string[] | null {
  const c = String(code ?? '').trim()
  if (!c) return null
  for (const province of tree) {
    if (sameRegionCode(province.code, c)) return [String(province.code)]
    for (const city of province.prefecture_level ?? []) {
      if (sameRegionCode(city.code, c)) return [String(province.code), String(city.code)]
      for (const county of city.county_level ?? []) {
        if (sameRegionCode(county.code, c)) {
          return [String(province.code), String(city.code), String(county.code)]
        }
      }
    }
  }
  return null
}

export function findRegionLabelsJoinedPath(
  tree: RegionProvinceNode[],
  codes: string[],
): string | null {
  const labels = findRegionLabelsByCodePath(tree, codes)
  return labels ? labels.join('-') : null
}

/** 级联 options：value 为区划码，label 为地名（与列表筛选 areaCode 一致） */
export interface RegionCascaderOptionNode {
  label: string
  value: string
  children?: RegionCascaderOptionNode[]
}

/**
 * 从级联 options 树按 value 路径解析 label（与用户在 Cascader 中点选的路径一致，避免与 regionTree 码值类型不一致导致解析失败）
 */
export function findRegionLabelsByCascaderPath(
  options: RegionCascaderOptionNode[],
  codes: string[],
): string[] | null {
  const trimmed = codes.map((s) => String(s ?? '').trim()).filter(Boolean)
  if (!trimmed.length) return null
  const labels: string[] = []
  let current: RegionCascaderOptionNode[] = options
  for (let i = 0; i < trimmed.length; i++) {
    const code = trimmed[i]
    const hit = current.find((n) => String(n.value) === String(code))
    if (hit === undefined) return null
    labels.push(String(hit.label))
    if (i >= trimmed.length - 1) break
    const next = hit.children
    if (!next?.length) return null
    current = next
  }
  return labels
}

function adminTierToPrefixLevel(tier: AdminDivisionTier): 1 | 2 | 3 | null {
  if (tier === 'province') return 1
  if (tier === 'city') return 2
  if (tier === 'district') return 3
  return null
}

/**
 * 9 位行政区划码按所选层级截成查询前缀，与库中 domestic_address_code 做 LIKE 前缀匹配：
 * - 选省：前 2 位（如 52）
 * - 选市：前 4 位（如 5203）
 * - 选区县：前 6 位（如 520302）
 *
 * 优先按末级码形态推断层级（市级/区级账号树深不足 3 时，路径长度不等于行政层级）。
 */
export function truncateAdministrativeCodeForPrefix(
  fullCode: string,
  /** 级联选中层级：1 省 2 市 3 区县；码形态可识别时忽略此项 */
  level?: 1 | 2 | 3,
): string {
  const digits = String(fullCode ?? '').replace(/\D/g, '')
  if (!digits) return ''
  const nine = digits.length >= 9 ? digits.slice(0, 9) : digits.padEnd(9, '0')
  const inferred = adminTierToPrefixLevel(resolveAdminDivisionTier(nine))
  const useLevel = inferred ?? level ?? 3
  if (useLevel === 1) return nine.slice(0, 2)
  if (useLevel === 2) return nine.slice(0, 4)
  return nine.slice(0, 6)
}

export function mapRegionTreeToCascaderOptions(
  tree: RegionProvinceNode[],
  valueKind: 'code' | 'name' = 'code',
): RegionCascaderOptionNode[] {
  const valueOf = (code: string, name: string) => (valueKind === 'name' ? name : code)
  return tree.map((province) => {
    const cities = (province.prefecture_level ?? []).map((city) => {
      const counties = (city.county_level ?? []).map((county) => ({
        label: county.name,
        value: valueOf(county.code, county.name),
      }))
      return {
        label: city.name,
        value: valueOf(city.code, city.name),
        ...(counties.length ? { children: counties } : {}),
      }
    })
    return {
      label: province.province,
      value: valueOf(province.code, province.province),
      ...(cities.length ? { children: cities } : {}),
    }
  })
}

/** 内存缓存：同一页面内多处调用 `fetchRegionTree` 只打一次 `administrativeAreaTree` */
let regionTreeCache: RegionProvinceNode[] | undefined = undefined
let regionTreeInflight: Promise<RegionProvinceNode[]> | null = null

/** 地区数据变更后调用，使下次 `fetchRegionTree` 重新请求 */
export function invalidateRegionTreeCache(): void {
  regionTreeCache = undefined
  regionTreeInflight = null
}

/**
 * 请求行政区划树（`administrativeAreaTree`，根码为本地 `user_regionCode`，缺省为贵州省根 520000000）；
 * 失败时返回空数组（不抛错，便于列表筛选等场景静默降级）。
 */
export async function fetchRegionTree(): Promise<RegionProvinceNode[]> {
  if (regionTreeCache !== undefined) return Promise.resolve(regionTreeCache)
  if (regionTreeInflight) return regionTreeInflight
  regionTreeInflight = (async () => {
    try {
      const roots = await requestAdministrativeAreaTree(getRootAdministrativeCodeForTree())
      const tree = administrativeResponseToProvinces(roots)
      // 空树不写入缓存，避免接口曾失败或解析异常时整页长期「暂无地区数据」
      if (tree.length > 0) {
        regionTreeCache = tree
      }
      return tree
    } catch {
      return []
    } finally {
      regionTreeInflight = null
    }
  })()
  return regionTreeInflight
}

/**
 * 管理页等场景：直接请求行政区划树，失败时抛出
 */
export async function fetchRegionTreeStrict(): Promise<RegionProvinceNode[]> {
  const roots = await requestAdministrativeAreaTree(getRootAdministrativeCodeForTree())
  return administrativeResponseToProvinces(roots)
}

/** 新增地区：POST /biz/overseas/globalcountry/area/add（失败且为路径 404 时回退 common） */
export interface CreateRegionPayload {
  /** 上级地区 geonameid（省或市） */
  parentGeonameid: number
  name: string
  /** 行政区划代码（9 位数字；6 位会在请求前补 000） */
  code: string
}

export async function createRegion(payload: CreateRegionPayload): Promise<void> {
  const adminDivisionCode = normalizeAdminDivisionCodeToNine(payload.code)
  if (!/^\d{9}$/.test(adminDivisionCode)) {
    throw new ApiError('行政区划代码须为 9 位数字', 400)
  }
  await postWithPathFallback(AREA_ADD_PATHS, {
    parentGeonameid: payload.parentGeonameid,
    cnName: payload.name.trim(),
    adminDivisionCode,
  })
  invalidateRegionTreeCache()
}

/** 修改地区：POST /biz/overseas/globalcountry/area/edit（失败且为路径 404 时回退 common） */
export interface UpdateRegionPayload {
  geonameid: number
  name: string
  /** 行政区划代码（9 位数字；6 位会在请求前补 000） */
  code: string
  /** 英文名称（可选） */
  enName?: string
  /** 排序号，越小越靠前（可选） */
  sortNumber?: number | null
}

export async function updateRegion(payload: UpdateRegionPayload): Promise<void> {
  const adminDivisionCode = normalizeAdminDivisionCodeToNine(payload.code)
  if (!/^\d{9}$/.test(adminDivisionCode)) {
    throw new ApiError('行政区划代码须为 9 位数字', 400)
  }
  const body: Record<string, unknown> = {
    geonameid: payload.geonameid,
    cnName: payload.name.trim(),
    adminDivisionCode,
  }
  if (payload.enName != null) {
    body.enName = String(payload.enName).trim() || undefined
  }
  if (payload.sortNumber !== undefined) {
    body.sortNumber = payload.sortNumber
  }
  await postWithPathFallback(AREA_EDIT_PATHS, body)
  invalidateRegionTreeCache()
}

/** 地区详情：GET /biz/overseas/globalcountry/area/detail */
export interface AreaDetailResult {
  geonameid: number
  cnName: string
  enName: string
  adminDivisionCode: string
  sortNumber: number | null
  regionalLevel: number | null
  parentGeonameid: number | null
  countryCode: string
}

const AREA_DETAIL_PATHS = [
  '/biz/overseas/globalcountry/area/detail',
  '/biz/overseas/common/globalcountry/area/detail',
] as const

export async function fetchAreaDetail(geonameid: number): Promise<AreaDetailResult | null> {
  const id = Number(geonameid)
  if (!Number.isFinite(id) || id <= 0) return null
  const ordered = orderPathsByPreferredBase(AREA_DETAIL_PATHS)
  for (let i = 0; i < ordered.length; i++) {
    const path = ordered[i]!
    const isLast = i === ordered.length - 1
    try {
      const raw = await http.get<Record<string, unknown>>(path, {
        params: { geonameid: id },
        skipErrorMessage: true,
      })
      if (!raw || typeof raw !== 'object') return null
      const sortRaw = raw.sortNumber ?? raw.sort_number
      let sortNumber: number | null = null
      if (sortRaw != null && sortRaw !== '') {
        const n = Number(sortRaw)
        if (Number.isFinite(n)) sortNumber = n
      }
      const levelRaw = raw.regionalLevel ?? raw.regional_level
      let regionalLevel: number | null = null
      if (levelRaw != null && levelRaw !== '') {
        const n = Number(levelRaw)
        if (Number.isFinite(n)) regionalLevel = n
      }
      const parentRaw = raw.parentGeonameid ?? raw.parent_geonameid
      let parentGeonameid: number | null = null
      if (parentRaw != null && parentRaw !== '') {
        const n = Number(parentRaw)
        if (Number.isFinite(n)) parentGeonameid = n
      }
      return {
        geonameid: id,
        cnName: String(raw.cnName ?? raw.cn_name ?? '').trim(),
        enName: String(raw.enName ?? raw.en_name ?? '').trim(),
        adminDivisionCode: String(raw.adminDivisionCode ?? raw.admin_division_code ?? '').trim(),
        sortNumber,
        regionalLevel,
        parentGeonameid,
        countryCode: String(raw.countryCode ?? raw.country_code ?? '').trim(),
      }
    } catch (e) {
      if (!isLast && isMissingApiPathError(e)) continue
      throw e
    }
  }
  return null
}

/** 删除地区：POST /biz/overseas/globalcountry/area/delete（失败且为路径 404 时回退 common） */
export async function deleteRegion(geonameid: number): Promise<void> {
  if (!Number.isFinite(geonameid)) return
  await postWithPathFallback(AREA_DELETE_PATHS, [{ geonameid }])
  invalidateRegionTreeCache()
}

export async function fetchRegionNameOptions(): Promise<string[]> {
  const regions = await fetchRegionTree()
  return flattenRegionNames(regions)
}
